import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useSettings } from "@/providers/SettingsProvider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatInTimeZone } from "date-fns-tz";
import { EventObject } from "./types";
import { Trash2 } from "lucide-react";
import { RelationPicker } from "./RelationPicker";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventId: string, eventData: { 
    title: string; 
    start: Date; 
    end: Date; 
    description?: string;
    color?: string;
    relations?: {
      candidates: string[];
      offers: string[];
      companies: string[];
      matches: string[];
      contacts: string[];
    };
  }) => void;
  onDelete: (eventId: string) => void;
  event?: EventObject;
}

const EVENT_COLORS = [
  { value: "#9b87f5", label: "Purple" },
  { value: "#1EAEDB", label: "Blue" },
  { value: "#F97316", label: "Orange" },
  { value: "#D946EF", label: "Pink" },
  { value: "#0EA5E9", label: "Sky" },
  { value: "#8E9196", label: "Gray" },
];

export const EditEventModal = ({ isOpen, onClose, onSave, onDelete, event }: EditEventModalProps) => {
  const { timezone } = useSettings();
  const userTimezone = timezone || 'UTC';
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [color, setColor] = useState(EVENT_COLORS[0].value);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  const [selectedRelations, setSelectedRelations] = useState({
    candidates: [] as string[],
    offers: [] as string[],
    companies: [] as string[],
    matches: [] as string[],
    contacts: [] as string[]
  });
  
  // Initialize form state when modal opens or event changes
  useEffect(() => {
    if (event) {
      console.log("Event metadata in EditEventModal:", event.metadata);
      setTitle(event.title);
      setDescription(event.description || "");
      setStartDate(new Date(event.start));
      setEndDate(new Date(event.end));
      setColor(event.color || EVENT_COLORS[0].value);
      setConfirmDelete(false);
      
      // Initialize relations from event metadata
      setSelectedRelations({
        candidates: Array.isArray(event.metadata?.candidates) ? [...event.metadata.candidates] : [],
        offers: Array.isArray(event.metadata?.offers) ? [...event.metadata.offers] : [],
        companies: Array.isArray(event.metadata?.companies) ? [...event.metadata.companies] : [],
        matches: Array.isArray(event.metadata?.matches) ? [...event.metadata.matches] : [],
        contacts: Array.isArray(event.metadata?.contacts) ? [...event.metadata.contacts] : []
      });
    }
  }, [event, isOpen]);

  const handleRelationSelect = (type: string, id: string) => {
    setSelectedRelations(prev => ({
      ...prev,
      [type]: [...prev[type as keyof typeof prev], id]
    }));
  };

  const handleRelationRemove = (type: string, id: string) => {
    setSelectedRelations(prev => {
      console.log(`Removing ${id} from ${type}`, prev[type as keyof typeof prev]);
      return {
        ...prev,
        [type]: prev[type as keyof typeof prev].filter(itemId => itemId !== id)
      };
    });
  };

  const formatDateForInput = (date: Date) => {
    try {
      return formatInTimeZone(date, userTimezone, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      console.error("Error formatting date:", error);
      return date.toISOString().slice(0, 16);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!event) return;
    
    try {
      if (!title.trim()) {
        toast.error("Event title is required");
        return;
      }
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        toast.error("Invalid date format");
        return;
      }
      
      if (endDate <= startDate) {
        toast.error("End time must be after start time");
        return;
      }
      
      onSave(event.id, {
        title,
        start: startDate,
        end: endDate,
        description,
        color,
        relations: selectedRelations
      });
      
      onClose();
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error("Error updating event. Please check your input.");
    }
  };

  const handleDeleteClick = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    
    if (event) {
      onDelete(event.id);
      onClose();
    }
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[900px] w-[90vw] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-6 overflow-hidden">
          {/* Left side - Event details */}
          <ScrollArea className="h-[calc(80vh-180px)] pr-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start">Start ({userTimezone})</Label>
                  <Input
                    id="start"
                    type="datetime-local"
                    value={formatDateForInput(startDate)}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="end">End ({userTimezone})</Label>
                  <Input
                    id="end"
                    type="datetime-local"
                    value={formatDateForInput(endDate)}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="color">Color</Label>
                <Select value={color} onValueChange={setColor}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_COLORS.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: color.value }}
                          />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="h-32"
                />
              </div>
            </form>
          </ScrollArea>

          {/* Right side - Relation picker */}
          <div className="border-l pl-6 h-[calc(80vh-180px)] overflow-hidden">
            <Label className="mb-2 block">Related Items</Label>
            <RelationPicker
              selectedRelations={selectedRelations}
              onSelect={handleRelationSelect}
              onRemove={handleRelationRemove}
            />
          </div>
        </div>

        <div className="flex justify-between mt-2">
          <Button 
            type="button" 
            variant="destructive"
            onClick={handleDeleteClick}
            className="flex items-center"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {confirmDelete ? "Click again to confirm" : "Delete Event"}
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
